import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Pagination from "@mui/material/Pagination";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useMatch, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import useActivePage from "../../hooks/useActivePage";
import useCompanies, { CompaniesSortingType } from "../../hooks/useCompanies";
import useCompaniesWithFocusArea from "../../hooks/useCompaniesWithFocusArea";
import useCompaniesWithGlobalFilters from "../../hooks/useCompaniesWithGlobalFilters";
import useNavigateToCompany from "../../hooks/useNavigateToCompany";
import usePagination from "../../hooks/usePagination";
import { useLocationStore, usePipedriveStore } from "../../index";
import { GlobalFiltersTab } from "../../store/FilterSettingsStore";
import { formatNumber } from "../../utils/utils";
import GlobalFiltersIcon from "../icons/GlobalFiltersIcon";
import SearchIcon from "../icons/SearchIcon";
import tabStyles from "../List.module.css";
import Spinner from "../spinners/Spinner";
import styles from "./Companies.module.css";
import CompanyInfo from "./CompanyInfo";

function Companies() {
    const { id } = useParams();
    const isCompanyPage = useMatch("/companies/*");
    const navigateToCompany = useNavigateToCompany();
    const pipedriveStore = usePipedriveStore();
    const locationStore = useLocationStore();
    const [searchFilter, setSearchFilter] = useState("");
    const [debouncedSearchFilter] = useDebounce(searchFilter, 500);
    const [sortBy, setSortBy] = useState(CompaniesSortingType.CreatedNewest);
    const [page, setPage] = useState(1);
    const [hoveredCompanyId, setHoveredCompanyId] = useState<number | null>(null);
    const companies = useCompaniesWithGlobalFilters(useCompaniesWithFocusArea(useCompanies(debouncedSearchFilter, sortBy)));
    const [pageCount, pagedCompanies] = usePagination(companies, page, () => setPage(1));
    useActivePage(companies, Number(id), isCompanyPage && id && companies.find(c => c.id === Number(id)), searchFilter, null, sortBy, setPage);

    return (
        <div className={tabStyles.container}>
            <div className={tabStyles.header}>
                {!pipedriveStore.isOrganizationsLoading.fetching && formatNumber(companies.length)} Companies
                <div className={styles.visibilityHeaderIcon}>
                    {!!locationStore.checkedCompanyIds.length ? (
                        <VisibilityOffIcon onClick={e => locationStore.setCheckedCompanyIds([])} />
                    ) : (
                        <VisibilityIcon onClick={e => locationStore.setCheckedCompanyIds(companies.map(c => c.id))} />
                    )}
                </div>
                <GlobalFiltersIcon tab={GlobalFiltersTab.Companies} className={tabStyles.headerButton} />
            </div>
            <div className={tabStyles.filters}>
                <div className={tabStyles.search}>
                    <SearchIcon />
                    <input autoFocus placeholder="Search companies.." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
                </div>
                <div className={tabStyles.dropdowns}>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value={CompaniesSortingType.LocationsAsc}>Locations (# - ###)</option>
                        <option value={CompaniesSortingType.LocationsDesc}>Locations (### - #)</option>
                        <option value={CompaniesSortingType.DealsAsc}>Deals (# - ###)</option>
                        <option value={CompaniesSortingType.DealsDesc}>Deals (### - #)</option>
                        <option value={CompaniesSortingType.LeadsAsc}>Leads (# - ###)</option>
                        <option value={CompaniesSortingType.LeadsDesc}>Leads (### - #)</option>
                        <option value={CompaniesSortingType.LeaseCompsAsc}>Lease Comps (# - ###)</option>
                        <option value={CompaniesSortingType.LeaseCompsDesc}>Lease Comps (### - #)</option>
                        <option value={CompaniesSortingType.SaleCompsAsc}>Sale Comps (# - ###)</option>
                        <option value={CompaniesSortingType.SaleCompsDesc}>Sale Comps (### - #)</option>
                        <option value={CompaniesSortingType.TIMsAsc}>TIMs (# - ###)</option>
                        <option value={CompaniesSortingType.TIMsDesc}>TIMs (### - #)</option>
                        <option value={CompaniesSortingType.CreatedNewest}>Newest</option>
                        <option value={CompaniesSortingType.CreatedOldest}>Oldest</option>
                    </select>
                </div>
            </div>
            <div className={tabStyles.list}>
                {pipedriveStore.isOrganizationsLoading.fetching ? (
                    <Spinner className={tabStyles.spinner} />
                ) : (
                    pagedCompanies.map(o => {
                        const isChecked = locationStore.checkedCompanyIds.includes(o.id);
                        return (
                            <CompanyInfo
                                key={o.id}
                                id={o.id}
                                name={o.name}
                                className={[
                                    styles.item, tabStyles.item,
                                    isCompanyPage && id === o.id.toString() ? tabStyles.opened : null,
                                ].join(" ")}
                                onClick={() => navigateToCompany(o.id)}
                                controls={[
                                    <div
                                        key="visibilityControl"
                                        className={[
                                            styles.visibilityIcon,
                                            !isChecked ? styles.hiddenByDefault : "",
                                        ].join(" ")}
                                        data-title={isChecked ? "Hide on map" : "Show on map"}
                                        onMouseEnter={() => setHoveredCompanyId(o.id)}
                                        onMouseLeave={() => setHoveredCompanyId(prev => (prev === o.id ? null : prev))}
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (isChecked) {
                                                locationStore.setCheckedCompanyIds(locationStore.checkedCompanyIds.filter(id => id !== o.id));
                                            } else {
                                                locationStore.setCheckedCompanyIds([...locationStore.checkedCompanyIds, o.id]);
                                            }
                                        }}
                                    >
                                        {hoveredCompanyId === o.id && isChecked ? (
                                            <VisibilityOffIcon />
                                        ) : (
                                            <VisibilityIcon />
                                        )}
                                    </div>
                                ]}
                            />
                        )

                    })
                )}
            </div>
            <div className={tabStyles.pagination}>
                <Pagination count={pageCount} page={page} onChange={(_, page) => setPage(page)} size="small" />
            </div>
        </div>
    )
}

export default observer(Companies);